import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { staggerContainer, staggerItem } from '../../utils/animation';
import { PORTFOLIO_ITEMS, PERSONAL_INFO } from '../../data/constants';
import { IconExternalLink, IconGithub, IconArrowRight } from '../ui/Icons';
import './Portfolio.css';

const CATEGORY_COLORS: Record<string, string> = {
  backend:   '#6366F1',
  fullstack: '#8B5CF6',
  frontend:  '#A78BFA',
};

const FILTERS = ['all', 'backend', 'fullstack'];

export const Portfolio: React.FC = () => {
  const { ref, inView } = useIntersectionObserver({ threshold: 0.04, triggerOnce: true });
  const [active, setActive] = useState('all');

  const filtered = active === 'all'
    ? PORTFOLIO_ITEMS
    : PORTFOLIO_ITEMS.filter(p => p.category === active);

  return (
    <section id="portfolio" className="portfolio section" ref={ref}>
      <div className="container">
        <div className="section__header">
          <span className="section__subtitle">SELECTED WORK</span>
          <h2 className="section__title">Featured Projects</h2>
          <p className="section__description">
            Enterprise systems built for scale - AI pipelines, data platforms, and cloud-native
            architectures serving production workloads across Asia.
          </p>
        </div>

        <div className="portfolio__filters">
          {FILTERS.map(f => (
            <motion.button
              key={f}
              className={`portfolio__filter ${active === f ? 'portfolio__filter--active' : ''}`}
              onClick={() => setActive(f)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            className="portfolio__grid"
            variants={staggerContainer}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
          >
            {filtered.map((item, i) => {
              const accent     = CATEGORY_COLORS[item.category] ?? '#6366F1';
              const isFeatured = active === 'all' && i === 0;

              return (
                <motion.article
                  key={item.id}
                  className={[
                    'proj__card',
                    isFeatured ? 'proj__card--featured' : '',
                  ].join(' ').trim()}
                  variants={staggerItem}
                  whileHover={{ y: -5, transition: { type: 'spring', stiffness: 260, damping: 22 } }}
                  style={{ '--accent': accent } as React.CSSProperties}
                >
                  <div className="proj__accent-bar" />

                  <div className="proj__body">
                    <div className="proj__meta">
                      <span className="proj__num">0{i + 1}</span>
                      <span className="proj__badge">{item.category}</span>
                      <div className="proj__links">
                        {item.live && (
                          <a href={item.live} target="_blank" rel="noopener noreferrer"
                            className="proj__icon-link" aria-label="Live">
                            <IconExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Metric highlight for featured */}
                    {isFeatured && item.metric && (
                      <div className="proj__metric-row">
                        <span className="proj__metric-val">{item.metric}</span>
                        <span className="proj__metric-lbl">{item.metricLabel}</span>
                      </div>
                    )}

                    <h3 className="proj__title">{item.title}</h3>
                    <p className="proj__desc">{item.description}</p>

                    {/* Non-featured metric badge */}
                    {!isFeatured && item.metric && (
                      <div className="proj__metric-badge">
                        <span className="proj__metric-badge-val">{item.metric}</span>
                        <span className="proj__metric-badge-lbl">{item.metricLabel}</span>
                      </div>
                    )}

                    <div className="proj__stack">
                      {item.technologies
                        .slice(0, isFeatured ? item.technologies.length : 4)
                        .map((t, ti) => (
                          <span key={ti} className="proj__chip">{t}</span>
                        ))}
                      {!isFeatured && item.technologies.length > 4 && (
                        <span className="proj__chip proj__chip--more">
                          +{item.technologies.length - 4}
                        </span>
                      )}
                    </div>

                    <div className="proj__footer">
                      <span>View case</span>
                      <IconArrowRight size={13} />
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <div className="portfolio__cta">
          <motion.a
            href={PERSONAL_INFO.github}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--ghost"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <IconGithub size={15} /> View GitHub
          </motion.a>
        </div>
      </div>
    </section>
  );
};
